import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';

const exemplosAmbientes = [
  {
    icon: 'ri-user-received-line',
    titulo: 'Recepção',
    exemplo: '"Recepção com 1 recepcionista e sala de espera para 6 pessoas"',
    itens: ['Balcão de recepção', 'Cadeira operacional', 'Poltronas de espera', 'Mesa de centro'],
    cor: 'bg-amber-50 border-amber-100',
    iconCor: 'text-amber-600 bg-amber-100',
  },
  {
    icon: 'ri-building-line',
    titulo: 'Diretoria',
    exemplo: '"Sala de diretoria para 1 diretor com mesa de reunião para 6"',
    itens: ['Mesa executiva', 'Cadeira executiva', 'Mesa de reunião', 'Cadeiras de reunião'],
    cor: 'bg-slate-50 border-slate-100',
    iconCor: 'text-slate-600 bg-slate-100',
  },
  {
    icon: 'ri-team-line',
    titulo: 'Open Space / Staff',
    exemplo: '"Sala de staff com 12 colaboradores em open space"',
    itens: ['Estações de trabalho', 'Cadeiras ergonômicas NR-17', 'Gaveteiros', 'Armários'],
    cor: 'bg-emerald-50 border-emerald-100',
    iconCor: 'text-emerald-600 bg-emerald-100',
  },
  {
    icon: 'ri-presentation-line',
    titulo: 'Sala de Reunião',
    exemplo: '"Sala de reunião para 10 pessoas com projetor"',
    itens: ['Mesa de reunião', 'Cadeiras de reunião', 'Aparador', 'Suporte para TV/projetor'],
    cor: 'bg-rose-50 border-rose-100',
    iconCor: 'text-rose-600 bg-rose-100',
  },
  {
    icon: 'ri-cup-line',
    titulo: 'Copa / Refeitório',
    exemplo: '"Copa com 4 mesas para refeição de 16 pessoas"',
    itens: ['Mesas de refeitório', 'Cadeiras ou banquetas', 'Balcão de apoio', 'Armários de cozinha'],
    cor: 'bg-orange-50 border-orange-100',
    iconCor: 'text-orange-600 bg-orange-100',
  },
  {
    icon: 'ri-archive-drawer-line',
    titulo: 'Arquivo / Almoxarifado',
    exemplo: '"Arquivo morto com estantes para documentos"',
    itens: ['Estantes de aço', 'Armários de aço', 'Gaveteiros de aço', 'Lockers'],
    cor: 'bg-gray-50 border-gray-100',
    iconCor: 'text-gray-600 bg-gray-100',
  },
];

const passos = [
  {
    numero: '01',
    titulo: 'Descreva seu espaço',
    descricao: 'Informe a metragem total e os ambientes que você precisa: recepção, salas, open space, copa...',
    icon: 'ri-chat-3-line',
  },
  {
    numero: '02',
    titulo: 'Diga quantas pessoas',
    descricao: 'Para cada ambiente, informe quantos colaboradores vão trabalhar e quantas pessoas serão atendidas.',
    icon: 'ri-group-line',
  },
  {
    numero: '03',
    titulo: 'Receba o projeto estimado',
    descricao: 'A IA monta uma lista completa de mobiliário com quantidades e valores médios de mercado.',
    icon: 'ri-file-list-3-line',
  },
  {
    numero: '04',
    titulo: 'Solicite o orçamento real',
    descricao: 'Com a estimativa em mãos, nossos consultores elaboram um projeto 3D e orçamento personalizado.',
    icon: 'ri-whatsapp-line',
  },
];

export default function ConsultoriaIAPage() {
  useSEO({
    title: 'Consultor de Escritório com IA | Projeto e Orçamento Estimado',
    description: 'Use nossa IA para descobrir quais móveis você precisa para seu escritório. Descreva os ambientes, número de colaboradores e receba uma estimativa completa de mobiliário e valores.',
    keywords: 'consultor escritório IA, projeto escritório inteligência artificial, orçamento mobiliário corporativo, dimensionar escritório, móveis corporativos pernambuco',
    canonical: 'https://homeofficepremium.com/consultoria-ia',
    ogImage: 'https://homeofficepremium.com/og-image.jpg',
    robots: 'index, follow',
    dateModified: '2026-04-27',
  });

  useEffect(() => {
    const WIDGET_ID = 'readdy-agent-script';
    const WIDGET_SRC = 'https://readdy.ai/api/public/assistant/widget?projectId=d36a1ed6-70b0-4b48-a6ac-0cd315aa8ee9';

    // Injeta o script do widget apenas nesta página
    if (!document.getElementById(WIDGET_ID)) {
      const script = document.createElement('script');
      script.id = WIDGET_ID;
      script.src = WIDGET_SRC;
      script.setAttribute('mode', 'hybrid');
      script.setAttribute('voice-show-transcript', 'true');
      script.setAttribute('theme', 'light');
      script.setAttribute('size', 'compact');
      script.setAttribute('accent-color', '#E8610A');
      script.setAttribute('button-base-color', '#111111');
      script.setAttribute('button-accent-color', '#FFFFFF');
      script.setAttribute('main-label', 'Consultor de Escritório');
      script.setAttribute('empty-chat-message', 'Opa, tudo bom? Que massa que você chamou a Home Office Premium! Vamos montar esse projeto para ficar de primeira. Me conta: onde vai ser o escritório e qual a metragem total? Aí eu já vou te guiando com estimativas de valor para cada ambiente.');
      document.body.appendChild(script);
    }

    // Abre o widget automaticamente após 2s
    const timer = setTimeout(() => {
      const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
      if (btn) btn.click();
    }, 2000);

    return () => {
      clearTimeout(timer);
      // Remove o script e todos os elementos do widget ao sair da página
      const script = document.getElementById(WIDGET_ID);
      if (script) script.remove();
      // Remove elementos injetados pelo widget no DOM
      document.querySelectorAll<HTMLElement>(
        '[id*="readdy"], [class*="readdy"], [id*="vapi-widget"], [class*="vapi-widget"], [id*="agent-widget"], iframe[src*="readdy"]'
      ).forEach(el => el.remove());
    };
  }, []);

  const handleAbrirIA = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  const schemaService = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Consultoria de Escritório com Inteligência Artificial',
    description: 'Ferramenta de IA que ajuda empresas a dimensionar o mobiliário corporativo necessário para seus escritórios, com estimativas de quantidades e valores de mercado.',
    provider: {
      '@type': 'Organization',
      name: 'Home Office Premium',
      url: 'https://homeofficepremium.com',
    },
    serviceType: 'Consultoria de Mobiliário Corporativo',
    areaServed: 'Pernambuco, Brasil',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
      description: 'Consultoria gratuita via IA',
    },
  };

  const schemaBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://homeofficepremium.com/' },
      { '@type': 'ListItem', position: 2, name: 'Consultor de Escritório com IA', item: 'https://homeofficepremium.com/consultoria-ia' },
    ],
  };

  return (
    <main className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaService) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaBreadcrumb) }} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#111111] pt-20 pb-0">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        {/* Gradiente laranja sutil no fundo */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#E8610A]/10 rounded-full blur-3xl"></div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <nav className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-10">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <span className="text-gray-400">Consultor com IA</span>
          </nav>

          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#E8610A]/10 border border-[#E8610A]/20 text-[#E8610A] text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-wider">
              <span className="w-2 h-2 bg-[#E8610A] rounded-full animate-pulse"></span>
              Inteligência Artificial
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Seu Consultor de Escritório<br />
              <span className="text-[#E8610A]">com Inteligência Artificial</span>
            </h1>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-10">
              Descreva seu espaço em linguagem natural — quantos m², quais ambientes, quantas pessoas — e nossa IA monta um <strong className="text-white">projeto completo com lista de mobiliário e estimativa de valores</strong>.
            </p>

            <button
              onClick={handleAbrirIA}
              className="inline-flex items-center gap-3 bg-[#E8610A] text-white text-base font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-[#E8610A]/20 hover:shadow-[#E8610A]/30 hover:scale-105"
            >
              <i className="ri-sparkling-line text-lg"></i>
              Iniciar Consultoria Gratuita
              <i className="ri-arrow-right-line text-base"></i>
            </button>
            <p className="text-gray-600 text-xs mt-3">Gratuito · Sem cadastro · Resultado em segundos</p>
          </div>

          {/* Preview do chat — decorativo */}
          <div className="max-w-2xl mx-auto bg-white/5 border border-white/10 rounded-t-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
              <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A] rounded-full">
                <i className="ri-sparkling-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Consultor Home Office Premium</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                  <p className="text-emerald-400 text-xs">Online agora</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {/* Mensagem da IA */}
              <div className="flex gap-3">
                <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[#E8610A]/20 rounded-full mt-0.5">
                  <i className="ri-sparkling-line text-[#E8610A] text-xs"></i>
                </div>
                <div className="bg-white/10 rounded-2xl rounded-tl-none px-4 py-3 max-w-xs">
                  <p className="text-white text-sm leading-relaxed">Olá! Vou te ajudar a dimensionar o mobiliário do seu escritório. Me conta: qual é a metragem total do espaço?</p>
                </div>
              </div>

              {/* Resposta do usuário */}
              <div className="flex gap-3 justify-end">
                <div className="bg-[#E8610A] rounded-2xl rounded-tr-none px-4 py-3 max-w-xs">
                  <p className="text-white text-sm leading-relaxed">São 120m². Vou ter recepção, sala de diretoria, open space com 8 pessoas e uma sala de reunião.</p>
                </div>
              </div>

              {/* Resposta da IA */}
              <div className="flex gap-3">
                <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[#E8610A]/20 rounded-full mt-0.5">
                  <i className="ri-sparkling-line text-[#E8610A] text-xs"></i>
                </div>
                <div className="bg-white/10 rounded-2xl rounded-tl-none px-4 py-3 max-w-sm">
                  <p className="text-white text-sm leading-relaxed">Ótimo! Para a recepção, quantas pessoas vão trabalhar e qual a capacidade da sala de espera?</p>
                </div>
              </div>

              {/* Indicador de digitação */}
              <div className="flex gap-3">
                <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[#E8610A]/20 rounded-full">
                  <i className="ri-sparkling-line text-[#E8610A] text-xs"></i>
                </div>
                <div className="bg-white/10 rounded-2xl rounded-tl-none px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleAbrirIA}
              className="w-full mt-5 flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#C4500A] transition-colors cursor-pointer"
            >
              <i className="ri-chat-3-line"></i>
              Continuar essa conversa →
            </button>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Como funciona</h2>
          <p className="text-gray-500 max-w-xl mx-auto">Em menos de 5 minutos você tem uma estimativa completa do seu projeto</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {passos.map((passo) => (
            <div key={passo.numero} className="relative">
              <div className="bg-gray-50 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-bold text-gray-100">{passo.numero}</span>
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                    <i className={`${passo.icon} text-[#E8610A] text-lg`}></i>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{passo.titulo}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{passo.descricao}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Exemplos de ambientes */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Exemplos de ambientes</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Descreva qualquer combinação desses ambientes para a IA</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {exemplosAmbientes.map((amb) => (
              <div key={amb.titulo} className={`border rounded-xl p-5 ${amb.cor}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${amb.iconCor}`}>
                    <i className={`${amb.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-semibold text-gray-900">{amb.titulo}</h3>
                </div>
                <p className="text-xs text-gray-500 italic mb-3 leading-relaxed">{amb.exemplo}</p>
                <div className="flex flex-wrap gap-1.5">
                  {amb.itens.map((item) => (
                    <span key={item} className="text-xs bg-white/70 text-gray-600 px-2 py-1 rounded-full border border-white">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA central */}
      <section className="max-w-3xl mx-auto px-6 py-16 md:py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-[#E8610A]/10 rounded-2xl mx-auto mb-6">
          <i className="ri-sparkling-2-line text-[#E8610A] text-3xl"></i>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
          Pronto para descobrir o que seu escritório precisa?
        </h2>
        <p className="text-gray-500 mb-8 leading-relaxed max-w-xl mx-auto">
          Nossa IA foi treinada com dados reais de projetos corporativos e valores de mercado atualizados. Você recebe uma estimativa honesta e detalhada — de graça.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handleAbrirIA}
            className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#C4500A] transition-all duration-200 whitespace-nowrap cursor-pointer hover:scale-105"
          >
            <i className="ri-sparkling-line text-base"></i>
            Iniciar Consultoria com IA
          </button>
          <a
            href="https://wa.me/5581982030285?text=Oi%2C%20quero%20um%20projeto%20de%20escrit%C3%B3rio%20personalizado"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 bg-white text-gray-700 border border-gray-200 text-sm font-medium px-8 py-4 rounded-full hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-[#25D366] text-base"></i>
            Falar com consultor humano
          </a>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
          <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-500"></i> 100% gratuito</span>
          <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-500"></i> Sem cadastro</span>
          <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-500"></i> Resultado imediato</span>
          <span className="flex items-center gap-1.5"><i className="ri-check-line text-emerald-500"></i> Valores reais de mercado</span>
        </div>
      </section>

      {/* Links relacionados */}
      <section className="border-t border-gray-100 py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {[
              { label: 'Calculadora de Espaço', path: '/calculadora', icon: 'ri-calculator-line' },
              { label: 'Glossário Corporativo', path: '/glossario', icon: 'ri-book-open-line' },
              { label: 'Perguntas Frequentes', path: '/faq', icon: 'ri-questionnaire-line' },
              { label: 'Projetos Turnkey', path: '/turnkey', icon: 'ri-building-line' },
              { label: 'Solicitar Orçamento', path: '/contato', icon: 'ri-mail-send-line' },
            ].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#E8610A] transition-colors px-4 py-2 rounded-full border border-gray-100 hover:border-[#E8610A]/20 hover:bg-[#E8610A]/5"
              >
                <i className={`${link.icon} text-sm`}></i>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}